package dev.kuml.portal.web.editor

/**
 * Pure allow/deny rules for inlining a `GET /render` SVG response directly into the app page (R-27,
 * kUML Portal "Interactive Diagram-Editing UI" wave) -- the DOM-touching half (`DOMParser` +
 * tree-walk) lives in `SvgInliner.kt`, kept separate so this policy itself is directly unit-testable
 * without a browser DOM.
 *
 * ## Why this exists at all
 * `RenderResult.Success`'s own KDoc (`kuml-portal-render`) is explicit: the returned SVG string is
 * safe to SERVE with `Content-Type: image/svg+xml`, but is NOT safe to inline unescaped into an HTML
 * page -- SVG can carry `<script>`/event-handler content, and prior to this wave the editor only
 * ever displayed it as `<img src="blob:...">`, which sandboxes it for free (an `<img>` never
 * executes script/event-handler content, no matter what the SVG contains). R-27 needs a REAL `<svg>`
 * element in the DOM (for `getBBox()`-based selection overlays and `g[data-kuml-id]` hit-testing,
 * neither of which an `<img>` can provide) -- inlining it therefore reopens exactly the XSS surface
 * that `<img>` closed by construction, and this policy plus `SvgInliner.kt`'s tree-walk exist purely
 * to close it back up.
 *
 * ## Fail-safe id handling (K-4 of the implementation plan)
 * Verified against kUML v0.54.1's actual `SvgDocument`/`SvgInlineArrow`: a UML class-diagram render
 * uses ONLY `.kuml-*` class selectors in its `<defs>` and inline geometry for arrowheads -- there is
 * no `url(#...)` reference anywhere in a real render today, so rewriting every `id` attribute to
 * `data-kuml-id` is safe (no `getElementById`/`url(#...)` collision risk with the host page, e.g. an
 * id colliding with something named `"kportal"`). [needsIdPrefixing] is the defensive check against
 * a FUTURE kUML version reintroducing `url(#...)` (e.g. a marker-based arrowhead) without this
 * policy being updated in lockstep: if the raw SVG text contains `url(#`, the safe id-rewrite scheme
 * above would silently break marker references, so the caller (`SvgInliner.kt`) must fall back to
 * prefixing every id (and rewriting every `url(#...)` reference alongside it) instead of a flat
 * `id -> data-kuml-id` rename.
 */
public object SvgSanitizePolicy {
    /**
     * Elements allowed to survive the sanitizing tree-walk. Deliberately an ALLOW-list, not a
     * deny-list of [forbiddenTags] alone -- an allow-list fails closed against any element this
     * policy's author never anticipated (a future SVG spec addition, a renderer bug that emits an
     * unexpected tag), whereas a deny-list only ever fails closed against threats someone already
     * thought to name. [forbiddenTags] exists purely as executable documentation of the concrete
     * threats this allow-list was written to exclude -- [isAllowedTag] never consults it.
     */
    public val allowedTags: Set<String> =
        setOf(
            "svg",
            "g",
            "defs",
            "style",
            "rect",
            "circle",
            "ellipse",
            "line",
            "polyline",
            "polygon",
            "path",
            "text",
            "tspan",
            "title",
            "desc",
            "marker",
            "clippath",
        )

    /** Executable documentation only -- see [allowedTags]'s KDoc for why [isAllowedTag] does not
     * consult this set directly. Every one of these is a genuine, real-world SVG XSS vector. */
    public val forbiddenTags: Set<String> =
        setOf("script", "foreignobject", "use", "image", "iframe", "animate", "set", "handler")

    /** Case-insensitive: SVG/XML tag names are conventionally lower-case, but a hostile payload
     * cannot rely on this policy trusting that convention. */
    public fun isAllowedTag(tagName: String): Boolean = tagName.lowercase() in allowedTags

    /**
     * `true` for any attribute safe to keep on an allowed element. Rejects:
     *  - every `on*` event-handler attribute (`onload`, `onclick`, `onerror`, ... -- case-insensitive,
     *    an attacker-controlled SVG could otherwise execute script on virtually any user interaction
     *    or lifecycle event without ever needing a `<script>` tag at all);
     *  - `href`/`xlink:href` unless the value starts with `#` (a same-document fragment reference,
     *    e.g. a `<use>`-free internal anchor) -- rejects `javascript:`, `data:`, and any external URL,
     *    which could otherwise navigate the page or load attacker-controlled content on click/render.
     */
    public fun isAllowedAttribute(
        name: String,
        value: String,
    ): Boolean {
        val lower = name.lowercase()
        if (lower.startsWith("on")) return false
        if (lower == "href" || lower == "xlink:href") return value.startsWith("#")
        return true
    }

    /** Overload for callers that only have the attribute name (e.g. deciding whether to even read
     * its value) -- delegates to the two-argument overload with an empty value, which is safe for
     * every rule above except the `href`/`xlink:href` case, where an empty value is correctly
     * rejected too (an empty href is never a legitimate same-document fragment reference). */
    public fun isAllowedAttribute(name: String): Boolean = isAllowedAttribute(name, "")

    /**
     * A `url(` fragment reference, tolerating the CSS/SVG-legal forms a literal `"url(#"` substring
     * check misses: `url(#id)` (no quotes), `url("#id")`, `url('#id')`, arbitrary whitespace between
     * `url(` and the `#`/quote (`url( #id )`), AND the XML-entity-escaped quote forms an attribute
     * value must use when its own delimiter is that same quote character -- `url(&quot;#id&quot;)`
     * for a double-quote-delimited attribute (`&#39;`/`&apos;` for a single-quote-delimited one).
     * [rawSvg] is raw, still-serialized SVG TEXT here, never yet XML-entity-decoded, so a plain `"`/
     * `'` literal in the pattern alone would miss exactly the entity-escaped case a real renderer
     * emits in practice. Kept as a `Regex` constant rather than inlined into [needsIdPrefixing] so
     * its equivalence-class cases stay documented in one place (see [needsIdPrefixing]'s own KDoc
     * "Review fix" note, 2026-09-02).
     */
    private val URL_FRAGMENT_REFERENCE: Regex = Regex("""url\(\s*(?:['"]|&quot;|&#39;|&apos;)?\s*#""")

    /**
     * `true` if [rawSvg] contains a `url(...)` fragment reference anywhere -- the K-4 fail-safe
     * trigger (see this object's class KDoc). A [URL_FRAGMENT_REFERENCE] regex match, not a full
     * CSS/SVG parse: this only needs to decide "does the safe flat id-rewrite scheme apply, or must
     * every id be prefixed instead", and a false positive (falling back to prefixing when it was not
     * strictly necessary) is harmless -- only a false NEGATIVE (missing a real `url(#...)`
     * reference) would be a correctness bug.
     *
     * **Review fix (MINOR, 2026-09-02):** an earlier revision used a literal `rawSvg.contains
     * ("url(#")` substring check and claimed here that "a literal substring match cannot produce
     * [a false negative] for this specific pattern" -- that claim was wrong. CSS/SVG also permit
     * `url("#id")`, `url('#id')`, `url( #id )` (whitespace before the `#`/quote), and, since a real
     * renderer's SVG attribute value is XML-entity-escaped, `url(&quot;#id&quot;)` -- none of which
     * contain the literal substring `"url(#"`. A future kUML version emitting e.g.
     * `marker-end="url(&quot;#arrow&quot;)"` would have made [needsIdPrefixing] return `false`,
     * silently choosing the flat id-rewrite scheme, breaking every marker reference in the inlined
     * SVG -- precisely the K-4 fail-safe this function exists to trigger.
     */
    public fun needsIdPrefixing(rawSvg: String): Boolean = URL_FRAGMENT_REFERENCE.containsMatchIn(rawSvg)

    /**
     * A single rule's `selector-list { declarations }` -- used by [isSafeStyleContent] to walk a
     * `<style>` element's text content one rule at a time. Deliberately built ONLY from negated
     * character classes (`[^{}]`), never a literal `.` metacharacter, so it needs no dotall/`(?s)`
     * mode to match across the embedded newlines real, multi-rule CSS contains -- `(?s)` is a
     * JVM/PCRE inline-flag construct this policy avoids entirely, since this file compiles to a
     * plain JS `RegExp` (Kotlin/JS target) where it is not valid regex syntax at all.
     */
    private val STYLE_RULE: Regex = Regex("""([^{}]+)\{([^{}]*)\}""")

    /**
     * A single class selector segment, e.g. `.kuml-class` -- verified against kUML v0.48.0's actual
     * `SvgDocument.kt` CSS builder: every rule it ever emits selects exactly one `.kuml-*` class,
     * nothing else (no combinators, no element/universal/id/attribute selectors, no pseudo-classes).
     * A `{sel1, sel2, ...}` chain of `.`-prefixed segments (`.kuml-node.kuml-selected`) is tolerated
     * for robustness even though today's renderer never emits one.
     *
     * Security-review finding (MEDIUM, 2026-09-02): the pattern previously accepted ANY
     * `[A-Za-z][A-Za-z0-9-]*` class name, not just `kuml-`-prefixed ones, despite this KDoc already
     * claiming the prefix was enforced. The Portal UI's own CSS uses the `kportal-` prefix
     * throughout (`kuml-portal-web/src/jsMain/resources/css/app.css`), so a `<style>` selector
     * like `.kportal-auth-card` would have passed this check, [UNSAFE_DECLARATION_CHARS], and
     * [STYLE_RULE] alike -- restyling the whole host page once inlined (see [isSafeStyleContent]'s
     * KDoc for the full UI-redress/clickjacking rationale). Now requires the literal `kuml-`
     * prefix, matching what kUML's own renderer actually emits.
     */
    private val SAFE_CLASS_SELECTOR: Regex = Regex("""^(\.kuml-[A-Za-z][A-Za-z0-9-]*)+$""")

    /**
     * Characters with no legitimate place in one of kUML's own generated declaration blocks --
     * colors are hex (`#rrggbb`), sizes are bare numbers with a `px`/`pt` unit, font stacks are
     * comma-separated unquoted names, `stroke-dasharray` is space-separated numbers -- but each one
     * of which is load-bearing for turning a CSS declaration into an injection: `(`/`)` for
     * `url(...)`/`expression(...)`/`calc(...)`, `{`/`}` for smuggling in a second, unvalidated rule,
     * `@` for an at-rule (`@import`, `@media`, ...) disguised as a declaration, and `<`/`>`/`"`/`'`/
     * `\` for breaking out of the `<style>` text node into surrounding markup entirely.
     */
    private val UNSAFE_DECLARATION_CHARS: Regex = Regex("""[(){}@<>"'\\]""")

    /**
     * `true` iff [css] -- a `<style>` element's raw text content -- consists ENTIRELY of rules this
     * policy recognises as safe: every selector a bare `.kuml-*`-style class chain
     * ([SAFE_CLASS_SELECTOR]), every declaration block free of [UNSAFE_DECLARATION_CHARS], and no
     * leftover text outside a recognised `{...}` block at all (rejects anything [STYLE_RULE] cannot
     * account for, e.g. a CSS comment or a bare declaration with no `{}` wrapper -- fails closed by
     * construction, the same allow-list posture as [isAllowedTag]/[isAllowedAttribute]).
     *
     * ## Why this exists (HARDENING, currently NOT exploitable -- security-review finding, 2026-09-02)
     * A `<style>` element inlined into the HOST PAGE (not served standalone as `image/svg+xml`) is
     * NOT scoped to the SVG it lives in -- its rules apply document-wide, so a `<style>` containing
     * e.g. `a { color: red !important; }` would restyle the entire Portal UI, not just the diagram
     * (a UI-redress/clickjacking vector). [SvgInliner]'s tree-walk previously validated `<style>`'s
     * TAG and ATTRIBUTES like any other allowed element but never inspected its TEXT content at all.
     * Verified NOT currently reachable: [dev.kuml.portal.render.PortalRenderer.renderModel] hard-codes
     * the theme to `KumlBrandTheme()`, whose entire CSS block is built purely from theme constants
     * (colours, font stacks, stroke widths) -- there is no code path today from untrusted DSL text
     * into this `<style>` block. This closes the gap defensively anyway, for the moment some future
     * theme/customization feature lets ANY user-controlled string (a custom font family, a
     * stereotype-derived class name, ...) reach it: a payload that does not match kUML's own
     * `.kuml-*`-only, function-free CSS shape is dropped (the whole `<style>` element, see
     * [SvgInliner]'s call site) rather than trusted.
     */
    public fun isSafeStyleContent(css: String): Boolean {
        val trimmed = css.trim()
        if (trimmed.isEmpty()) return true
        var consumedLength = 0
        for (match in STYLE_RULE.findAll(trimmed)) {
            if (match.range.first != consumedLength) return false // unrecognised text before this rule
            consumedLength = match.range.last + 1
            val selectors = match.groupValues[1].split(",").map { it.trim() }
            if (selectors.any { !SAFE_CLASS_SELECTOR.matches(it) }) return false
            if (UNSAFE_DECLARATION_CHARS.containsMatchIn(match.groupValues[2])) return false
        }
        return consumedLength == trimmed.length // no unrecognised trailing text either
    }

    // Deliberately no structural-id blocklist here any more (review fix, MINOR round 2, 2026-09-02
    // -- reverts the round-1 fix below). kuml-io-svg 0.44.0 does emit `<g id="nodes">`, `<g
    // id="edges">`, and `<g id="kuml-diagram-frame">` alongside the real per-classifier ids, and a
    // round-1 revision of this policy stripped exactly those three ids in `SvgInliner.sanitize` so a
    // click on the diagram frame/title would not resolve to a fake node selection. That blocklist
    // was both too narrow and too wide: too narrow because a kUML comment's `<g id="<commentId>">`
    // (a real, reachable id -- `DslSystemPrompt.kt` documents `comment(...)` as a first-class DSL
    // construct) was never on it, and `DiagramOutline.kt` discards comments from `/outline` entirely,
    // so a click on one still resolved to a fake node selection exactly like the frame id used to;
    // too wide because it stripped the `id` of a real classifier legitimately named `"nodes"`,
    // `"edges"`, or `"kuml-diagram-frame"` (a valid, non-empty [DiagramLoroMutator] name -- there is
    // no reserved-word check), making it permanently unclickable/unhighlightable despite existing in
    // the outline. The actual fix belongs where the click is RESOLVED, not where the SVG is
    // sanitized: `DiagramCanvas.transitionFor` now validates every clicked id against the outline
    // before treating it as a node or edge selection at all -- see that function's own KDoc. Any id
    // that is not genuinely a member of the outline, structural-group ids and comment ids alike,
    // therefore produces no selection change, without this policy needing to know which renderer-
    // internal ids exist in any given kUML version.
}
